import React, { useEffect, useState } from 'react';
import {
  Button as ChakraButton,
  Flex,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Box,
  useToast,
  HStack,
  MenuButton,
  Menu as ChakraMenu,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Heading,
  Input,
  Pagination,
  StatusBadge,
  Table,
  Menu,
} from '~/components';
import { PAGE_SIZE, ROUTER_PATHS } from '~/constants';
import { useGroups, useDebounce, useQueryParams, getGroupsXlsx } from '~/hooks';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { handleDownload } from '~/utils/handleDownloadXlsx';
import { getUsersByGroupXlsx } from '~/services/Users';

export const Groups: React.VFC = () => {
  const queryParams = useQueryParams();
  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const history = useHistory();
  const location = useLocation();
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
    };
  });
  const { data, isLoading, isFetching, error, refetch } = useGroups(
    Number(parametros.pagina),
    parametros.busca,
    {},
    Number(parametros.paginacao),
  );
  const toast = useToast();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  useDebounce(
    () => {
      setParametros({ ...parametros, busca: searchTmp, pagina: '1' });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );
  useEffect(() => {
    refetch();
  }, []);
  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({ ...parametros, pagina: pagina ?? '1' });
    updateUrlParams({ ...parametros, pagina: pagina ?? '1' }, history);
  }, [location.search]);
  const handlePageSizeChange = (e: any) => {
    setParametros({ ...parametros, paginacao: e.target.value, pagina: '1' });
    updateUrlParams(
      { ...parametros, paginacao: e.target.value, pagina: '1' },
      history,
    );
  };
  return (
    <Box px="8" py="4">
      <Heading>Grupos</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={5}>
          <Flex align="center" gridGap={4}>
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              placeholder="Procure por nome"
              w={350}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              fontSize="15px"
            />
            {isFetching && <Spinner />}
          </Flex>
          <HStack>
            <ChakraMenu>
              <MenuButton
                as={ChakraButton}
                rightIcon={<ChevronDownIcon />}
                variant="outline"
              >
                Exportar
              </MenuButton>
              <MenuList>
                <MenuItem
                  disabled={isLoading || isLoadingDownload}
                  onClick={() =>
                    handleDownload(setIsLoadingDownload, getGroupsXlsx, toast, {
                      search: parametros.busca,
                    })
                  }
                >
                  Exportar grupos
                </MenuItem>
                <MenuItem
                  disabled={isLoading || isLoadingDownload}
                  onClick={() =>
                    handleDownload(
                      setIsLoadingDownload,
                      getUsersByGroupXlsx,
                      toast,
                      {
                        search: parametros.busca,
                      },
                    )
                  }
                >
                  Exportar usuários por Grupo
                </MenuItem>
              </MenuList>
            </ChakraMenu>
            <Link to={ROUTER_PATHS.PORTAL.ADMIN.GROUPS_FORM}>
              <Button>Cadastrar</Button>
            </Link>
          </HStack>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos grupos.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Segmento de venda</Th>
                  <Th>Responsáveis pela conta</Th>
                  <Th>Status</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(group => (
                    <Tr key={group.id_grupoeconomico}>
                      <Td>
                        <Link
                          to={`/portal/admin/groups/form?groupId=${group.id_grupoeconomico}`}
                        >
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {group.nm_grupoeconomico}
                          </Text>
                        </Link>
                      </Td>
                      <Td>{group.segmentos_venda || '-'}</Td>
                      <Td>{group.responsaveis || '-'}</Td>
                      <Td>
                        <StatusBadge variant={group.status ? 'green' : 'red'}>
                          {group.isActive}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuList>
                            <Link
                              to={`/portal/admin/groups/form?groupId=${group.id_grupoeconomico}`}
                            >
                              <MenuItem>Editar</MenuItem>
                            </Link>
                            <MenuItem
                              disabled={isLoading || isLoadingDownload}
                              onClick={() =>
                                handleDownload(
                                  setIsLoadingDownload,
                                  getUsersByGroupXlsx,
                                  toast,
                                  {
                                    id_grupoeconomico: group.id_grupoeconomico,
                                  },
                                )
                              }
                            >
                              Exportar Usuários
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              <Pagination
                totalCountOfRegisters={data?.count || 0}
                currentPage={Number(parametros.pagina) ?? 1}
                onPageChange={_page => {
                  setParametros({ ...parametros, pagina: _page.toString() });
                  updateUrlParams(
                    { ...parametros, pagina: _page.toString() },
                    history,
                  );
                }}
                onPageSizeChange={handlePageSizeChange}
                registersPerPage={Number(parametros.paginacao) ?? 10}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
